import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  Alert,
  TextField
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
  Download as DownloadIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import AdminPasswordDialog from '../components/AdminPasswordDialog';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [adminPasswordOpen, setAdminPasswordOpen] = useState(false);
  const [canDownload, setCanDownload] = useState(false);

  // Check if user is already an administrator
  const isAdministrator = user?.role && ['administrator', 'super_user'].includes(user.role);

  // Fetch installer info
  const { data: installerData } = useQuery({
    queryKey: ['installerLatest'],
    queryFn: () => apiClient.installer.getLatest(),
  });

  const handleDownloadInstaller = () => {
    // If user is already admin, download directly
    if (isAdministrator) {
      downloadInstaller();
    } else {
      // Otherwise, ask for admin password
      setAdminPasswordOpen(true);
    }
  };

  const handleAdminPasswordVerified = () => {
    setCanDownload(true);
    downloadInstaller();
  };

  const downloadInstaller = () => {
    const installerInfo = installerData?.data?.data;
    if (!installerInfo || !installerInfo.available) {
      alert('Installer is not available. Please contact your administrator.');
      return;
    }

    window.location.href = installerInfo.downloadUrl || `/api/installer/download/${installerInfo.version}`;
  };

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  const getRoleColor = (role: string) => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' } = {
      'super_user': 'error',
      'administrator': 'primary',
      'manager': 'secondary',
      'supervisor': 'info',
      'team_lead': 'success',
      'agent': 'default',
      'other': 'warning'
    };
    return colors[role] || 'default';
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        My Profile
      </Typography>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Personal Information Card */}
        <Card sx={{ flex: 1, minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              Personal Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Full Name
              </Typography>
              <Typography variant="body1">
                {user.firstName} {user.lastName}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Display Name
              </Typography>
              <Typography variant="body1">
                {user.displayName || `${user.firstName} ${user.lastName}`}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Display name can be edited in Users Management by administrators
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Employee ID
              </Typography>
              <Typography variant="body1">
                {user.employeeId}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Email Address
              </Typography>
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, fontSize: 16 }} />
                {user.email}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Role
              </Typography>
              <Chip 
                label={user.role.replace('_', ' ').toUpperCase()} 
                color={getRoleColor(user.role)}
                size="small"
                sx={{ mt: 0.5 }}
              />
            </Box>
          </CardContent>
        </Card>

        {/* Department Information Card */}
        <Card sx={{ flex: 1, minWidth: 300 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1 }} />
              Department Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Department
              </Typography>
              <Typography variant="body1">
                {user.department?.name || 'Not assigned'}
              </Typography>
            </Box>

            {user.department?.description && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1">
                  {user.department.description}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Security Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1 }} />
            Security
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body1" gutterBottom>
                Password
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Last changed: Not available
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={() => setChangePasswordOpen(true)}
            >
              Change Password
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Windows Agent Installer Section */}
      {installerData?.data?.data && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ComputerIcon sx={{ mr: 1 }} />
              Windows Agent Installer
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            {installerData.data.data.available ? (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Version: {installerData.data.data.version}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    File size: {(installerData.data.data.size / 1024).toFixed(0)} KB
                  </Typography>
                  {!isAdministrator && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Note:</strong> Administrator or super_user password required to download.
                      </Typography>
                    </Alert>
                  )}
                </Box>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadInstaller}
                  fullWidth
                  color="primary"
                >
                  Download TEKSTimeAgent-Setup-v{installerData.data.data.version}.msi
                </Button>
              </Box>
            ) : (
              <Alert severity="warning">
                <Typography variant="body2">
                  {installerData.data.data.message || 'Installer is not available. Please contact your administrator.'}
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
        onSuccess={() => {
          // Could show a success message here
          console.log('Password changed successfully');
        }}
      />

      <AdminPasswordDialog
        open={adminPasswordOpen}
        onClose={() => setAdminPasswordOpen(false)}
        onSuccess={handleAdminPasswordVerified}
      />
    </Container>
  );
};

export default ProfilePage;
